import { useEffect, useState } from "react";
import { api, fileToBase64 } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trash2, Upload } from "lucide-react";

export default function Journal() {
  const { t } = useLang();
  const [entries, setEntries] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [caption, setCaption] = useState("");
  const [weight, setWeight] = useState("");
  const [saving, setSaving] = useState(false);
  const [pair, setPair] = useState({ a: null, b: null });

  const load = () => api.get("/journal").then((r) => setEntries(r.data));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("Max 8MB");
    setPhoto(await fileToBase64(f));
  };

  const save = async () => {
    if (!photo) return toast.error("Upload a photo");
    setSaving(true);
    try {
      await api.post("/journal", { photo_base64: photo, caption, weight_kg: weight ? parseFloat(weight) : null });
      toast.success("Entry saved");
      setPhoto(null); setCaption(""); setWeight("");
      await load();
    } catch {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/journal/${id}`);
    load();
  };

  const first = entries[0];
  const last = entries[entries.length - 1];
  const compareA = pair.a ? entries.find((e) => e.id === pair.a) : first;
  const compareB = pair.b ? entries.find((e) => e.id === pair.b) : last;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="journal-page">
      <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">TRANSFORMATION LOG</div>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-2" data-testid="journal-title">{t.journal.title}</h1>
      <p className="text-slate-400 mt-2 max-w-2xl">{t.journal.subtitle}</p>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6 space-y-4">
          <label className="block cursor-pointer">
            <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="journal-file-input" />
            <div className="aspect-square rounded-lg border-2 border-dashed border-[#1E2638] hover:border-[#00F076]/60 flex items-center justify-center overflow-hidden">
              {photo ? <img src={photo} alt="preview" className="w-full h-full object-cover" /> : (
                <div className="text-center text-slate-500">
                  <Upload className="w-8 h-8 mx-auto mb-3" />
                  <div className="font-mono-tech text-xs tracking-wider uppercase">{t.journal.upload}</div>
                </div>
              )}
            </div>
          </label>
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.journal.caption}</Label>
            <Input value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="journal-caption-input" className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
          </div>
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.journal.weight}</Label>
            <Input type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} data-testid="journal-weight-input" className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
          </div>
          <Button onClick={save} disabled={saving || !photo} data-testid="journal-save-btn" className="w-full bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider">
            {saving ? "…" : t.journal.save}
          </Button>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6">
            <div className="font-display font-bold uppercase text-xl mb-4">Before / After</div>
            {entries.length < 2 ? (
              <div className="text-slate-500 text-sm">Need at least 2 entries to compare.</div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {[compareA, compareB].map((e, i) => e && (
                  <div key={i} className="relative rounded-lg overflow-hidden border border-[#1E2638]">
                    <img src={e.photo_data_url} alt="entry" className="w-full aspect-[3/4] object-cover" />
                    <div className="absolute top-2 left-2 rounded bg-[#00F076] text-black text-[10px] font-black uppercase px-2 py-0.5">
                      {i === 0 ? "BEFORE" : "AFTER"}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-[#090C10]/85 text-xs">
                      <div className="font-mono-tech tracking-wider">{new Date(e.created_at).toLocaleDateString()}</div>
                      {e.weight_kg && <div className="text-[#00F076]">{e.weight_kg} kg</div>}
                      {e.caption && <div className="text-slate-300 mt-0.5">{e.caption}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-6">
            <div className="font-display font-bold uppercase text-xl mb-4">{t.journal.entries}</div>
            {entries.length === 0 ? (
              <div className="text-slate-500 text-sm">—</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {entries.map((e) => (
                  <div key={e.id} className="relative rounded-lg overflow-hidden border border-[#1E2638] group" data-testid={`journal-entry-${e.id}`}>
                    <img src={e.photo_data_url} alt="entry" className="w-full aspect-square object-cover" />
                    <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-[#090C10]/85 text-[10px] font-mono-tech">
                      {new Date(e.created_at).toLocaleDateString()}
                      {e.weight_kg ? ` · ${e.weight_kg}kg` : ""}
                    </div>
                    <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition flex gap-1">
                      <button onClick={() => setPair({ ...pair, a: e.id })} className="text-[10px] bg-[#090C10]/90 px-1.5 py-0.5 rounded text-emerald-400" data-testid={`journal-set-a-${e.id}`}>A</button>
                      <button onClick={() => setPair({ ...pair, b: e.id })} className="text-[10px] bg-[#090C10]/90 px-1.5 py-0.5 rounded text-emerald-400" data-testid={`journal-set-b-${e.id}`}>B</button>
                      <button onClick={() => del(e.id)} className="text-[10px] bg-[#090C10]/90 px-1.5 py-0.5 rounded text-red-400" data-testid={`journal-delete-${e.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
